/**
 * Queue / Drop-in API client for the business app.
 *
 * Wraps backend/app/routers/queue.py endpoints behind the BFF proxy.
 * Types are the OpenAPI-generated aliases (ADR-005) — the single source of truth
 * is backend Pydantic → openapi.json → @bw/shared/types/generated. The enriched
 * QueueEntryResponse (ADR-025 Phase B, #1741) carries the "Called to court"
 * context — assignment_id / court_id / court_name / assigned_at / confirm_by /
 * checked_in — and CourtAssignmentResponse carries confirm_by + co_players.
 */
import { httpClient } from "@bw/shared/api"
import type { Schema } from "@bw/shared/types/generated"

// ────────────────────────────────────────────────────────────────────
// Types — generated aliases (no hand-written mirrors)
// ────────────────────────────────────────────────────────────────────

// Request-param unions (not part of the response schema). ``mode`` is a free
// string in the backend model; these narrow the console's own inputs.
export type QueueMode = "normal" | "ranked_singles" | "ranked_doubles"
export type QueueStatus = "waiting" | "assigned" | "cancelled" | "expired"

export type QueueEntry = Schema["QueueEntryResponse"]
export type QueueJoinRequest = Schema["QueueJoinRequest"]
export type CourtAssignment = Schema["CourtAssignmentResponse"]
export type QueueStats = Schema["QueueStatsResponse"]
export type Party = Schema["PartyResponse"]
export type PartyMembershipRecovery = Schema["PartyMembershipRecoveryResponse"]
export type QueueOperatorAction = Schema["QueueOperatorActionRequest"]
export type QueueReassignRequest = Schema["QueueReassignRequest"]

// ────────────────────────────────────────────────────────────────────
// Endpoints — Queue
// ────────────────────────────────────────────────────────────────────

export function getQueueStats(facilityId: string): Promise<QueueStats> {
  return httpClient.get<QueueStats>(`/queue/stats?facility_id=${encodeURIComponent(facilityId)}`)
}

export function listQueue(
  facilityId: string,
  status: QueueStatus = "waiting",
): Promise<QueueEntry[]> {
  return httpClient.get<QueueEntry[]>(
    `/queue?facility_id=${encodeURIComponent(facilityId)}&status=${encodeURIComponent(status)}`,
  )
}

export function joinQueue(req: QueueJoinRequest): Promise<QueueEntry> {
  return httpClient.post<QueueEntry>("/queue", req)
}

export function leaveQueue(entryId: string): Promise<void> {
  return httpClient.delete<void>(`/queue/${entryId}`)
}

export function skipWaitingEntry(
  entryId: string,
  body: QueueOperatorAction,
): Promise<QueueEntry> {
  return httpClient.post<QueueEntry>(`/queue/${entryId}/skip`, body)
}

export function removeWaitingEntry(
  entryId: string,
  body: QueueOperatorAction,
): Promise<void> {
  return httpClient.post<void>(`/queue/${entryId}/remove`, body)
}

export function getQueueStatus(entryId: string): Promise<QueueEntry> {
  return httpClient.get<QueueEntry>(`/queue/${entryId}`)
}

export function assignCourt(
  entryId: string,
  courtId: string,
  sessionDurationMinutes: number,
): Promise<CourtAssignment> {
  return httpClient.post<CourtAssignment>(`/queue/${entryId}/assign`, {
    court_id: courtId,
    session_duration_minutes: sessionDurationMinutes,
  })
}

// (frictionless drop-in 2026-07-02: confirmAssignmentCheckin removed — a called
// party is on court immediately; POST /queue/assignments/{id}/checkin is gone.)

// Staff "End session" — free a live court early so the queue refills (ADR-025
// Phase B, #1741). 204 on success; 404 {code: NO_LIVE_SESSION} on an idle court.
export function endSession(
  courtId: string,
  facilityId: string,
  body: QueueOperatorAction,
): Promise<void> {
  return httpClient.post<void>(
    `/queue/courts/${encodeURIComponent(courtId)}/end-session?facility_id=${encodeURIComponent(facilityId)}`,
    body,
  )
}

export function reassignCourt(
  assignmentId: string,
  body: QueueReassignRequest,
): Promise<CourtAssignment> {
  return httpClient.post<CourtAssignment>(
    `/queue/assignments/${encodeURIComponent(assignmentId)}/reassign`,
    body,
  )
}

export function closeFacilityQueue(facilityId: string): Promise<void> {
  return httpClient.post<void>(`/queue/facility/${facilityId}/close`)
}

export function reopenFacilityQueue(facilityId: string): Promise<void> {
  return httpClient.post<void>(`/queue/facility/${facilityId}/reopen`)
}

export function clearFacilityQueue(facilityId: string): Promise<void> {
  return httpClient.delete<void>(`/queue/facility/${facilityId}/clear`)
}

// ────────────────────────────────────────────────────────────────────
// Endpoints — Parties (walk-in group play)
// ────────────────────────────────────────────────────────────────────

export function createParty(facilityId: string): Promise<Party> {
  return httpClient.post<Party>("/queue/party", { facility_id: facilityId })
}

export function joinParty(code: string): Promise<Party> {
  return httpClient.post<Party>("/queue/party/join", { code })
}

export function leaveParty(partyId: string): Promise<void> {
  return httpClient.delete<void>(`/queue/party/${partyId}`)
}

export function getParty(partyId: string): Promise<Party> {
  return httpClient.get<Party>(`/queue/party/${partyId}`)
}

export function listPartyMemberships(
  facilityId: string,
  userId: string,
): Promise<PartyMembershipRecovery[]> {
  const params = new URLSearchParams({
    facility_id: facilityId,
    user_id: userId,
  })
  return httpClient.get<PartyMembershipRecovery[]>(
    `/queue/party/memberships?${params.toString()}`,
  )
}

export function removePartyMembership(
  partyId: string,
  targetUserId: string,
): Promise<void> {
  return httpClient.delete<void>(
    `/queue/party/${encodeURIComponent(partyId)}/members/${encodeURIComponent(targetUserId)}`,
  )
}

export function requeuePartyForStaff(
  partyId: string,
  body: Schema["_PartyQueueBody"],
): Promise<QueueEntry> {
  return httpClient.post<QueueEntry>(
    `/queue/party/${encodeURIComponent(partyId)}/staff-queue`,
    body,
  )
}

export function queueParty(partyId: string, mode: QueueMode): Promise<QueueEntry> {
  return httpClient.post<QueueEntry>(`/queue/party/${partyId}/queue`, { mode })
}
